import { useEffect, useState } from 'react'
import { useIMask } from 'react-imask'
import { TextField } from '@mui/material'
import { PlatformEvent } from 'lib/util'
import { useMemoRef, useStyles } from 'platform/react/hook'
import { ClearAdornment } from 'ui/Element/Field/Text'

const style = (theme, { mask }) => ({
  root: {
    '& #clear': {
      display: 'none',
    },
    '& #clear:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:hover #clear': {
      display: 'inline-flex',
    },
    '& input:focus + #clear, & input:active + #clear': {
      display: 'inline-flex',
    },
  },
  input: {
    ...mask && {
      fontFamily: 'monospace',
    },
  },
})

/**
 * Lists possible dispatch methods used to decide which mask to be used according to the field's
 * value.
 *
 * @type {{startsWith: function(*, *): *}}
 */
const dispatchMethod = {
  startsWith: (appended, dynamicMasked) => {
    const value = dynamicMasked.value + appended
    return dynamicMasked.compiledMasks.find(mask => value.startsWith(mask.startsWith))
      || dynamicMasked.compiledMasks.find(mask => !mask.startsWith)
      || dynamicMasked.compiledMasks[0]
  },
}

/**
 * If set, attempts to use the `options`'s `dispatchMethod` to obtain a {@link dispatchMethod} and
 * returns `options` with it as `dispatch`. In any case, returns `options` without the
 * `dispatchMethod` property.
 *
 * @param {Object} options
 * @returns {{dispatch: *}|Omit<*, "dispatchMethod">}
 */
const prepared = (options) => {
  const { dispatchMethod: name, ...restOptions } = options

  return name && dispatchMethod[name]
    ? { ...restOptions, dispatch: dispatchMethod[name] }
    : restOptions
}

/**
 * MaskField Element
 *
 * Wrapper around {@link TextField}, extended with {@link useIMask} functionality to restrict, guide
 * or validate input values through masks and regular expressions.
 *
 * @param {React.Ref} forwardedRef    a forwarded reference object to the root element
 * @param {Object} [configuration]    optional {@link useIMask} hook configuration
 * @param {Object} [events]           element's event handlers
 * @param {Boolean} [focused]         whether or not the input should become focused
 * @param {boolean} [clearable]       whether to display the clear button when the field has value
 * @param {string} [value]            field content
 * @param {Object} [props]            additional element's properties
 * @param {string} [props.variant]    the input style variant
 * @param {string} [props.helperText] text displayed below the field
 * @param {boolean} [props.error]     whether the field is in error state
 * @param {boolean} [props.disabled]  whether the field cannot be interacted with
 * @param {Object} [props.InputProps] props for the underlying input element
 * @returns {JSX.Element}
 */
const MaskField = ({
  forwardedRef,
  configuration,
  events,
  focused,
  clearable,
  value: propsValue,
  ...props
}) => {
  const { validator, strict, ...options } = configuration || { mask: /.*/ }
  const { onChange, ...restEvents } = events || {}
  const [opts, setOpts] = useState(prepared(options))
  const classes = useStyles(style, options)()

  useEffect(() => {
    setOpts(prepared(options))
  }, [configuration])

  /**
   * Called whenever `mask` value changes.
   *
   * @param {string} newValue
   * @param {Object} [mask]
   */
  const onAccept = (newValue, mask) => {
    onChange?.(new PlatformEvent('change', { value: mask?.masked?.typedValue ?? newValue }))
  }

  const { ref, value, setValue } = useIMask(opts, { onAccept })

  useEffect(() => {
    setValue(propsValue || '')
  }, [propsValue])

  const clear = () => {
    const newValue = ''
    setValue(newValue)
    ref.current?.focus()
    onAccept(newValue, false)
  }

  return (
    <TextField
      {...props}
      {...restEvents}
      ref={forwardedRef}
      inputRef={ref}
      value={value}
      variant={props.variant || 'standard'}
      className={classes.root}
      fullWidth={true}
      autoFocus={focused}
      helperText={props.helperText || '\u00A0'}
      InputProps={{
        ...props.InputProps,
        className: classes.input,
        endAdornment: <>
          <ClearAdornment
            show={clearable && !props.disabled && !!value && value !== options?.mask}
            classes={classes}
            onClick={clear}
          />
          {props.InputProps?.endAdornment}
        </>,
      }}
    />
  )
}

export default useMemoRef(MaskField, props => [
  props.configuration,
  props.value,
  props.events,
  props.helperText,
  props.error,
])
